import React, { useCallback, useEffect, useState } from "react";
import { Alert, Box, Button, FormControl, Grid, IconButton, InputLabel, Select, Snackbar, TextField, Tooltip, Typography } from "@mui/material";
import { AddCircle as IconAddCircle, Block as IconBlock, Search as IconSearch } from "@mui/icons-material";
import { Link, useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import moment from "moment";

import atencionService from "../../services/atencionService";
import * as filialService from "../../services/filialService";
import DataGridBase from "../../components/common/DataGridBase";
import DialogYesNo from "../../components/DialogYesNo";
import { useMantenimiento } from "../../hooks/useMantenimiento";

// Navegabilidad de las atenciones, con los filtros principales: filial, persona y rango
// de fechas. Desde aca se anula una atencion, que queda con estado Anulado sin
// borrarse. El mantenimiento esta en su propia pagina (/atencion/edit/:id),
// donde se ven los datos y el detalle de la atencion.
//
// La atencion ya no tiene un tipo de servicio propio: la columna de servicios
// muestra los tipos de servicio de su detalle, concatenados por el backend.

// Estado Anulado: una atencion anulada no se puede anular otra vez.
const ESTADO_ANULADO = 4;

const Index = () => {
    const { isReadOnly } = useMantenimiento("atencion");
    const idFilialUsuario = useSelector((state) => state.seguridad?.idFilial) || 0;
    const navigate = useNavigate();

    const [filtros, setFiltros] = useState({
        persona: "",
        fechaInicio: "",
        fechaFin: "",
        idFilial: idFilialUsuario > 0 ? idFilialUsuario : 0,
    });
    const [listFiliales, setListFiliales] = useState([]);
    const [buscando, setBuscando] = useState(false);

    const [cargando, setCargando] = useState(true);
    const [results, setResults] = useState([]);

    const [openDialogAnular, setOpenDialogAnular] = useState(false);
    const [seleccionado, setSeleccionado] = useState(null);

    const [openMessage, setOpenMessage] = useState(false);
    const [propsMessage, setPropsMessage] = useState({ severity: "success", message: "" });

    const avisar = (severity, message) => {
        setPropsMessage({ severity, message });
        setOpenMessage(true);
    }

    const fetchResults = useCallback(async (filtrosBusqueda = filtros) => {
        setCargando(true);

        const data = await atencionService.getsAtencionView(filtrosBusqueda)
            .catch((err) => { console.log("Err: ", err); return []; });

        setResults(data || []);
        setCargando(false);
    }, [filtros])

    const fetchFiliales = async () => {
        const data = await filialService.getFilialesList().catch(() => []);
        setListFiliales(filialService.soloFilialDelUsuario(data, idFilialUsuario));
    }

    const buscar = () => {
        setBuscando(true);
        fetchResults(filtros).then(() => setBuscando(false));
    }

    useEffect(() => {
        fetchFiliales();
        const filialInicial = idFilialUsuario > 0 ? idFilialUsuario : 0;
        setFiltros((prev) => ({ ...prev, idFilial: filialInicial }));
        fetchResults({ ...filtros, idFilial: filialInicial });
    }, [idFilialUsuario])

    const anular = async () => {
        if (!seleccionado) return;

        const anulado = await atencionService.anularAtencion(seleccionado.id)
            .catch((err) => { console.log("Err: ", err); return false; });

        setSeleccionado(null);

        if (!anulado) {
            setOpenDialogAnular(false);
            avisar("error", "No se pudo anular la atencion.");
            return;
        }

        setOpenDialogAnular(false);
        avisar("success", "La atencion quedo anulada.");

        fetchResults(filtros);
    }

    const columns = [
        { field: 'id', headerName: 'ID', width: 50, minWidth: 45, sortable: true, headerAlign: 'center', align: 'center',
            renderCell: (p) => <span style={{ fontSize: '0.75rem', color: '#666', fontFamily: 'monospace' }}>{p.value != null ? p.value : p.row.id}</span> },
        { field: 'idAtencionNumero', headerName: 'N°', width: 75, sortable: false, headerAlign: 'center', align: 'center'
            , renderCell: (cellValues) => (
                <Link to={"/atencion/edit/" + cellValues.row.id} style={{ color: 'inherit' }}>
                    {String(cellValues.row.id).padStart(4, "0")}
                </Link>
            ) },
        { field: 'filial', headerName: 'Filial', width: 75, minWidth: 65, sortable: true, headerAlign: 'center', align: 'center'
            , renderCell: (p) => p.row.codigoFilial || p.row.filial || '' },
        { field: 'persona', headerName: 'Persona', width: 180, sortable: false, headerAlign: 'left' },
        { field: 'tiposServicio', headerName: 'Servicios', width: 260, sortable: false, headerAlign: 'left'
            , renderCell: (cellValues) => cellValues.row.tiposServicio || "" },
        { field: 'fecha', headerName: 'Fecha', width: 105, sortable: false, headerAlign: 'center', align: 'center'
            , renderCell: (cellValues) => cellValues.row.fecha ? moment(cellValues.row.fecha).format('DD/MM/YYYY') : '' },
        { field: 'cantidadDetalles', headerName: 'N° Detalle', width: 95, sortable: false, headerAlign: 'center', align: 'center' },
        { field: 'estado', headerName: 'Estado', width: 75, sortable: false, headerAlign: 'center', align: 'center' },
        { field: 'anular', headerName: '', width: 70, sortable: false, headerAlign: 'center', align: 'center'
            , renderCell: (cellValues) => (
                <Tooltip title={cellValues.row.idEstado == ESTADO_ANULADO ? "La atencion ya esta anulada" : "Anular atencion"}>
                    <span>
                        <IconButton aria-label={"anular atencion"} size="small"
                            disabled={isReadOnly || cellValues.row.idEstado == ESTADO_ANULADO}
                            onClick={() => { setSeleccionado(cellValues.row); setOpenDialogAnular(true); }}>
                            <IconBlock fontSize="small" />
                        </IconButton>
                    </span>
                </Tooltip>
            )
        },
    ];

    return (
        <div>
            <Grid container>
                <Grid item>
                    <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                        Atenciones
                    </Typography>
                </Grid>
                <Grid container spacing={1} padding={"12px 0px 0px 0px"}>
                    <Grid item xs={12} sm={12}>
                        <Grid container item xs={12} sm={12} spacing={1} padding={0}>
                            <Grid item xs={12} sm={3}>
                                <FormControl fullWidth>
                                    <InputLabel htmlFor="input-filialfiltro" shrink>Filial</InputLabel>
                                    <Select
                                        native
                                        label="Filial"
                                        value={filtros.idFilial || (idFilialUsuario > 0 ? idFilialUsuario : 0)}
                                        disabled={idFilialUsuario > 0}
                                        onChange={(event) => {
                                            const val = parseInt(event.target.value, 10) || 0;
                                            const nuevoFiltro = { ...filtros, idFilial: val };
                                            setFiltros(nuevoFiltro);
                                            fetchResults(nuevoFiltro);
                                        }}
                                        inputProps={{ name: 'idFilial', id: 'input-filialfiltro' }}
                                        size="small"
                                    >
                                        {idFilialUsuario <= 0 && <option key={0} value={0}>{"(Todas las filiales)"}</option>}
                                        {listFiliales.map((row) => (
                                            <option key={row.id} value={row.id}>{row.descripcion || row.nombre}</option>
                                        ))}
                                    </Select>
                                </FormControl>
                            </Grid>
                            <Grid item xs={8} sm={3}>
                                <TextField name="persona" label="Persona" value={filtros.persona} fullWidth
                                    onChange={(event) => setFiltros({ ...filtros, persona: event.target.value })}
                                    onKeyDown={(event) => { if (event.key === "Enter") { event.preventDefault(); buscar(); } }}
                                    size="small" />
                            </Grid>
                            <Grid item xs={6} sm={2}>
                                <TextField name="fechaInicio" label="Fecha Inicio" type="date" value={filtros.fechaInicio} fullWidth
                                    onChange={(event) => setFiltros({ ...filtros, fechaInicio: event.target.value })}
                                    size="small" InputLabelProps={{ shrink: true }} />
                            </Grid>
                            <Grid item xs={6} sm={2}>
                                <TextField name="fechaFin" label="Fecha Fin" type="date" value={filtros.fechaFin} fullWidth
                                    onChange={(event) => setFiltros({ ...filtros, fechaFin: event.target.value })}
                                    size="small" InputLabelProps={{ shrink: true }} />
                            </Grid>
                            <Grid item xs={4} sm={1} display={"flex"} alignItems={"center"}>
                                <Button variant={"outlined"} size={"medium"} fullWidth disabled={buscando} onClick={() => { buscar(); }}>
                                    <IconSearch />
                                </Button>
                            </Grid>
                        </Grid>
                    </Grid>
                    <Grid item xs={12} sm={12}>
                        <Box sx={{ display: 'grid', gridTemplateColumns: '1fr' }}>
                            <DataGridBase
                                rows={results}
                                columns={columns}
                                loading={cargando}
                                toolbarButtons={[
                                    {
                                        text: "Nuevo",
                                        icon: <IconAddCircle />,
                                        disabled: isReadOnly,
                                        onClick: () => navigate("/atencion/edit/0"),
                                    }
                                ]}
                            />
                        </Box>
                    </Grid>
                </Grid>
            </Grid>

            <DialogYesNo
                open={openDialogAnular}
                titulo={"Anular Atencion"}
                texto={"La atencion quedara con estado Anulado. No se borra, pero deja de estar vigente. Desea continuar?"}
                setYes={anular}
                setNo={() => { setSeleccionado(null); }}
                openClose={() => {
                    setOpenDialogAnular(!openDialogAnular);

                    if (openDialogAnular) setSeleccionado(null);
                }}
            />

            <Snackbar open={openMessage} autoHideDuration={5000} onClose={() => setOpenMessage(!openMessage)}
                anchorOrigin={{ vertical: "top", horizontal: "center" }}>
                <Alert onClose={() => setOpenMessage(!openMessage)} severity={propsMessage.severity}>
                    {propsMessage.message}
                </Alert>
            </Snackbar>
        </div>
    );
}

export default Index;
